// v3.99d.354 — Defensive UTC parser for server-emitted datetime strings.
//
// Root cause we defend against: legacy DB rows (and some historical write
// paths — MyCollectionPanel bulk-sale, admin datetime-local inputs) stored
// `auction_ends_at` as a bare ISO string (e.g. "2026-03-01T12:00:00")
// with no `Z`/`+00:00` suffix. JavaScript's `new Date("2026-03-01T12:00:00")`
// interprets bare ISO strings as LOCAL wall-time — so collectors in
// EST/PST browsers saw a countdown offset by their UTC delta (up to +8h).
//
// Server-side, the settlement worker always compared these values as UTC,
// so the auction actually closed at the wall-time value in UTC. The
// countdown mismatch was purely a client-side parsing bug.
//
// This helper coerces any bare (tz-less) ISO string to UTC before handing
// it to `Date`. If the input already includes a tz suffix (Z, +HH:MM,
// -HH:MM), it is returned to `Date` untouched. Non-string inputs are
// passed through so callers can safely use it for `Date` objects or
// numeric epoch values too.
//
// Usage:
//   const ms = parseServerDate(listing.auction_ends_at).getTime();
export function parseServerDate(value) {
  if (value == null) return new Date(NaN);
  if (value instanceof Date) return value;
  if (typeof value === "number") return new Date(value);
  if (typeof value !== "string") return new Date(value);
  // Strings with an explicit tz suffix ("Z", "+HH:MM", "-HH:MM" that
  // appears AFTER the "T") are already unambiguous.
  // Match: T<time>(Z | ±HH:MM | ±HHMM) at end of string
  const hasTz = /T[^+\-Z]*(Z|[+-]\d{2}:?\d{2})$/i.test(value);
  if (hasTz) return new Date(value);
  // Bare ISO — treat as UTC by appending Z.
  return new Date(value + "Z");
}

// Convenience: milliseconds-since-epoch parser (returns NaN on bad input).
export function parseServerDateMs(value) {
  return parseServerDate(value).getTime();
}
